import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client';
import { forkDashboard, type Dashboard } from '../../api/dashboards';
import { listDatasets, listVersions, type Dataset, type DatasetVersion } from '../../api/datasets';
import { Alert, Button, Card } from '../ui';
import './ForkCard.css';

export interface ForkCardProps {
  dashboard: Dashboard;
  /**
   * True when this dashboard's figures were summarised for you because the data
   * behind them is not yours to read (Phase 15). Then "the same data this one
   * uses" is not an option — a fork never carries access — so it is not offered.
   */
  isDerived?: boolean;
}

interface Choice {
  id: string;
  label: string;
}

/**
 * Ask the same question of your own data.
 *
 * The one thing this control must never imply is that copying somebody's
 * dashboard copies their data: it does not, and when the data behind the
 * original is not yours to read, the backend refuses rather than pointing your
 * copy at rows you cannot open. So the picker is the *primary* action here, not
 * an advanced option.
 */
export function ForkCard({ dashboard, isDerived = false }: ForkCardProps) {
  const navigate = useNavigate();
  const [choices, setChoices] = useState<Choice[]>([]);
  const [chosen, setChosen] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const page = await listDatasets();
        const ready: Choice[] = [];
        for (const dataset of page.results as Dataset[]) {
          const versions = await listVersions(dataset.id);
          const latest = versions.find((version: DatasetVersion) => version.status === 'READY');
          if (latest) {
            ready.push({ id: latest.id, label: `${dataset.name} · v${latest.version}` });
          }
        }
        if (!cancelled) setChoices(ready);
      } catch {
        if (!cancelled) setChoices([]);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const fork = async () => {
    setWorking(true);
    setError(null);
    try {
      const copy = await forkDashboard(dashboard.id, chosen || undefined);
      void navigate(`/dashboards/${copy.id}/edit`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setWorking(false);
    }
  };

  return (
    <Card
      title="Ask this of your own data"
      description="Forking copies the questions and the layout — never the data, and never a way into it."
    >
      {error && (
        <Alert tone="warning" title="That could not be forked">
          {error.message}
        </Alert>
      )}

      <div className="sc-fork">
        <label className="sc-fork__field">
          <span>Your data</span>
          <select value={chosen} onChange={(event) => setChosen(event.target.value)}>
            {isDerived ? (
              <option value="">Choose one of your datasets</option>
            ) : (
              <option value="">The same data this one uses</option>
            )}
            {choices.map((choice) => (
              <option key={choice.id} value={choice.id}>
                {choice.label}
              </option>
            ))}
          </select>
        </label>

        <Button size="sm" isLoading={working} onClick={() => void fork()}>
          Fork it
        </Button>
      </div>

      <p className="sc-fork__note">
        Your copy is private, and yours to change. Every block is checked against the data you
        choose, so anything that does not fit says which column is missing.
      </p>
    </Card>
  );
}
